import React from 'react';
import { 
  Calendar, Clock, User, Bookmark, Play, 
  TrendingUp, Eye, ThumbsUp, Award, Zap, Target,
  ChevronRight, Star, Users
} from 'lucide-react';
import { EnhancedAnalysis } from '../../types/enhancedAnalysis';

interface HeroSectionProps {
  analysis: EnhancedAnalysis;
  onBookmark: () => void;
  onStartReading: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  analysis,
  onBookmark,
  onStartReading
}) => {
  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyLabel = (level: string) => {
    switch (level) {
      case 'beginner': return '入门级';
      case 'intermediate': return '进阶级';
      case 'advanced': return '专家级';
      default: return '未知';
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 overflow-hidden">
      {/* 背景装饰 */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-200 to-transparent rounded-full blur-3xl opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-200 to-transparent rounded-full blur-3xl opacity-20"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
          {/* 主要内容 */}
          <div className="lg:col-span-2">
            {/* 趋势标签 */}
            {analysis.socialMetrics.trending && (
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-red-100 border border-orange-200 text-orange-700 rounded-full text-sm font-medium mb-6 animate-pulse">
                <TrendingUp className="w-4 h-4 mr-2" />
                <span className="font-semibold">热门分析</span>
                <span className="ml-2 text-orange-600">• 正在热议</span>
              </div>
            )}

            {/* 标题 */}
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {analysis.title}
            </h1>

            {/* 核心洞察摘要 */}
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl p-6 mb-8 shadow-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">核心洞察</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {analysis.summary.slice(0, 100)}
                    {analysis.summary.length > 100 && '...'}
                  </p>
                </div>
              </div>
            </div>

            {/* 元信息 */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8">
              {/* 作者信息 */}
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-medium">
                    {analysis.author.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900">{analysis.author.name}</span>
                  <span className="text-gray-500 ml-1">• {analysis.author.title}</span>
                </div>
              </div>

              {/* 发布时间 */}
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{new Date(analysis.created_at).toLocaleDateString('zh-CN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>

              {/* 阅读时间 */}
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>约 {analysis.readingTime} 分钟阅读</span>
              </div>

              {/* 难度等级 */}
              <div className="flex items-center space-x-1">
                <Award className="w-4 h-4" />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(analysis.difficultyLevel)}`}>
                  {getDifficultyLabel(analysis.difficultyLevel)}
                </span>
              </div>
            </div>

            {/* 社交指标 */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
              <div className="flex items-center space-x-1">
                <Eye className="w-4 h-4" />
                <span>{analysis.socialMetrics.views.toLocaleString()} 阅读</span>
              </div>
              <div className="flex items-center space-x-1">
                <ThumbsUp className="w-4 h-4" />
                <span>{analysis.socialMetrics.likes} 点赞</span>
              </div>
              {/* 移除分享统计 */}
              <div className="flex items-center space-x-1">
                <Bookmark className="w-4 h-4" />
                <span>{analysis.socialMetrics.bookmarks} 收藏</span>
              </div>
            </div>

            {/* 主要行动按钮 - 简化版本 */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={onStartReading}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group"
              >
                <Play className="w-5 h-5 mr-2 group-hover:translate-x-0.5 transition-transform" />
                开始阅读
                <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </button>

              {/* 简化的快速操作 */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={onBookmark}
                  className="p-3 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                  title="收藏分析"
                >
                  <Bookmark className="w-5 h-5" />
                </button>
                
                {/* 移除分享按钮 */}
              </div>
            </div>
          </div>

          {/* 侧边信息卡片 */}
          <div className="lg:col-span-1">
            <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
              {/* 分析框架 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">分析框架</h3>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{analysis.methodology.name}</div>
                    <div className="text-sm text-gray-500">{analysis.methodology.framework}</div>
                  </div>
                </div>
              </div>

              {/* 关键指标 */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">字数统计</span>
                  <span className="text-sm font-medium text-gray-900">
                    {analysis.wordCount.toLocaleString()} 字
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">分析维度</span>
                  <span className="text-sm font-medium text-gray-900">
                    {analysis.methodology.steps.length} 个
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">参与度</span>
                  <span className="text-sm font-medium text-gray-900">
                    {Math.round(analysis.socialMetrics.engagementRate * 100)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">推荐指数</span>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < 4 ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-sm font-medium text-gray-900 ml-1">4.0</span>
                  </div>
                </div>
              </div>

              {/* 社区活跃度 */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">社区讨论</h4>
                  <Users className="w-4 h-4 text-gray-400" />
                </div>
                <div className="text-xs text-gray-500">
                  {analysis.socialMetrics.comments} 条评论
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(analysis.socialMetrics.engagementRate * 1000, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 底部渐变分隔 */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
    </section>
  );
};

export default HeroSection;